import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LandingFooter } from "./LandingFooter";
import "./MarkdownPage.css";

interface TocItem {
  id: string;
  text: string;
  index: number;
}

interface Tab {
  label: string;
  path: string;
  icon: React.ReactNode;
}

export interface MarkdownPageProps {
  contentPath: (lang: string) => Promise<{ default: string }>;
  kicker?: string;
  tabs?: Tab[];
}

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l8 3v6c0 5-3.5 8-8 11-4.5-3-8-6-8-11V5z"/>
  </svg>
);
const DocIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <path d="M14 2v6h6M8 13h8M8 17h6"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="17" rx="2"/><path d="M3 9h18M8 2v4M16 2v4"/>
  </svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
  </svg>
);
const ArrowUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5M5 12l7-7 7 7"/>
  </svg>
);

const LEGAL_TABS: Tab[] = [
  { label: "Конфиденциальность", path: "/privacy", icon: <ShieldIcon /> },
  { label: "Соглашение", path: "/terms", icon: <DocIcon /> },
  { label: "Удаление данных", path: "/data-deletion", icon: <TrashIcon /> },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\wа-яё\s-]/gi, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

function extractToc(content: string): TocItem[] {
  const items: TocItem[] = [];
  let index = 0;
  for (const line of content.split("\n")) {
    const match = line.match(/^##\s+(.+)/);
    if (match) {
      const raw = match[1];
      const text = raw.replace(/^\d+\.\s*/, "");
      items.push({ id: slugify(raw), text, index: index++ });
    }
  }
  return items;
}

function extractMeta(content: string): { title: string; subtitle: string; readTime: number } {
  const lines = content.split("\n");
  let title = "";
  let subtitle = "";
  let wordCount = 0;
  for (const line of lines) {
    if (!title && line.startsWith("# ")) {
      title = line.replace(/^#\s+/, "");
    } else if (!subtitle && /^\*[^*].*[^*]\*$/.test(line.trim())) {
      subtitle = line.trim().replace(/^\*/, "").replace(/\*$/, "");
    } else {
      wordCount += line.split(/\s+/).filter(Boolean).length;
    }
  }
  return { title, subtitle, readTime: Math.max(1, Math.ceil(wordCount / 200)) };
}

export const MarkdownPage: React.FC<MarkdownPageProps> = ({ contentPath, kicker, tabs }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [content, setContent] = useState<string>("");
  const [activeId, setActiveId] = useState<string>("");
  const [showTop, setShowTop] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const lang = i18n.language.startsWith("ru") ? "ru" : "en";

  const activeTabs = tabs ?? (
    LEGAL_TABS.some(t => t.path === location.pathname) ? LEGAL_TABS : undefined
  );

  useEffect(() => {
    const load = (l: string) =>
      contentPath(l)
        .then((mod) => fetch(mod.default))
        .then((r) => r.text());
    load(lang).then(setContent).catch(() => load("en").then(setContent));
  }, [lang, contentPath]);

  const onScroll = useCallback(() => {
    const el = document.documentElement;
    const pct = el.scrollTop / (el.scrollHeight - el.clientHeight);
    if (progressRef.current) progressRef.current.style.width = `${pct * 100}%`;
    setShowTop(el.scrollTop > 400);

    const headings = document.querySelectorAll<HTMLElement>(".mdp-prose h2[id]");
    let current = "";
    headings.forEach(h => { if (h.getBoundingClientRect().top <= 120) current = h.id; });
    setActiveId(current);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const toc = extractToc(content);
  const { title, subtitle, readTime } = extractMeta(content);

  const titleWords = title.split(/\s+/);
  const titleMain = titleWords.slice(0, -1).join(" ");
  const titleItalic = titleWords[titleWords.length - 1] ?? "";

  const H2: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const text = typeof children === "string" ? children
      : Array.isArray(children) ? children.map(c => typeof c === "string" ? c : "").join("") : "";
    const clean = text.replace(/^\d+\.\s*/, "");
    const id = slugify(text);
    return <h2 id={id}>{clean}</h2>;
  };

  return (
    <div className="mdp">
      <div className="mdp-progress" ref={progressRef} aria-hidden="true" />

      <section className="mdp-head">
        <div className="mdp-wrap">
          <div className="mdp-head-inner">
            {kicker && <span className="mdp-kicker">{kicker}</span>}
            {title && (
              <h1 className="mdp-title">
                {titleMain} <span className="mdp-title-italic">{titleItalic}</span>
              </h1>
            )}
            {(subtitle || readTime > 0) && (
              <div className="mdp-meta">
                {subtitle && (
                  <span className="mdp-meta-item">
                    <CalendarIcon />
                    {subtitle}
                  </span>
                )}
                {subtitle && <span className="mdp-meta-dot" />}
                <span className="mdp-meta-item">
                  <ClockIcon />
                  Чтение ~ {readTime} мин
                </span>
              </div>
            )}
            {activeTabs && (
              <nav className="mdp-tabs" aria-label="Документы">
                {activeTabs.map(tab => (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    className={`mdp-tab${location.pathname === tab.path ? " mdp-tab--active" : ""}`}
                    aria-current={location.pathname === tab.path ? "page" : undefined}
                  >
                    {tab.icon}
                    {tab.label}
                  </Link>
                ))}
              </nav>
            )}
          </div>
        </div>
      </section>

      <section className="mdp-body">
        <div className="mdp-wrap">
          <div className={`mdp-grid${toc.length === 0 ? " mdp-grid--no-toc" : ""}`}>
            {toc.length > 0 && (
              <aside className="mdp-toc" aria-label="Содержание">
                <div className="mdp-toc-label">На этой странице</div>
                <ul className="mdp-toc-list">
                  {toc.map(item => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className={activeId === item.id ? "mdp-toc-active" : ""}
                      >
                        <span className="mdp-toc-num">
                          {String(item.index + 1).padStart(2, "0")}
                        </span>
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </aside>
            )}

            <article className="mdp-prose">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: () => null,
                  em: ({ children }) => {
                    const text = String(children ?? "");
                    if (/обновлено|updated|версия|version|\d{4}/i.test(text)) return null;
                    return <em>{children}</em>;
                  },
                  h2: H2 as any,
                  a: ({ href, children }) =>
                    href?.startsWith("/")
                      ? <Link to={href}>{children}</Link>
                      : <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
                }}
              >
                {content}
              </ReactMarkdown>
            </article>
          </div>
        </div>
      </section>

      <button
        className={`mdp-to-top${showTop ? " mdp-to-top--show" : ""}`}
        aria-label="Наверх"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <ArrowUpIcon />
      </button>

      <LandingFooter />
    </div>
  );
};
